import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import PushDeviceList from './PushDeviceList';
import {
  appleDeviceName, deviceKind, forgetPushDevice, isIOS, notificationPermission, pushDeviceId, pushSupport, turnOnPush,
} from '../../lib/push';

type Status = {
  webPushEnabled: boolean;
  workingDevices: number;
  thisDevice: { registered: boolean; working: boolean; lastSuccessAt: string | null; lastFailureAt: string | null };
};

/**
 * "Alerts on this phone / tablet / computer" — the top of Settings →
 * Notifications. The device is named for what it is (deviceKind).
 *
 * What it says is read from the API (GET /mobile/push/status for THIS device
 * id) plus the browser's own permission, never assumed: a device the server
 * disabled after failed sends reads "not working" even though the browser
 * still says Allowed. The buttons are the only places that ask for permission.
 *
 * `showDailyAlertTest`: the office mount also offers "Send today's daily alert
 * to me now" — today's alert, to the caller only, push first then text, as the
 * real alert would reach them (POST /notification-preferences/daily-job-alert/test).
 */
export default function PushDeviceCard({ showDailyAlertTest }: { showDailyAlertTest: boolean }) {
  const queryClient = useQueryClient();
  const support = pushSupport();
  const kind = deviceKind();
  const apple = appleDeviceName();
  const deviceId = pushDeviceId();
  const [busy, setBusy] = useState<null | 'on' | 'off' | 'test' | 'daily'>(null);
  const [permission, setPermission] = useState(notificationPermission());

  const { data: status, isLoading, isError, refetch } = useQuery({
    queryKey: ['push', 'status', deviceId],
    queryFn: async () => unwrap<Status>((await api.push.status(deviceId)).data),
    enabled: support !== 'unsupported',
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['push', 'status'] });

  const on = status?.thisDevice.working === true && permission === 'granted';

  let line: string;
  if (support === 'unsupported') line = 'This browser cannot receive alerts. Text messages still reach you if they are on below.';
  else if (support === 'ios_needs_home') line = `On ${apple}, alerts work from the Home Screen app: in Safari tap Share → Add to Home Screen, open FieldIQ from the new icon, sign in, and turn alerts on there.`;
  else if (status && !status.webPushEnabled) line = 'Phone alerts are not switched on for FieldIQ yet.';
  else if (permission === 'denied') line = isIOS()
    ? `Alerts are blocked for FieldIQ. Allow them in Settings → Notifications → FieldIQ on this ${apple}.`
    : 'Alerts are blocked for this site. Allow notifications for app.field-iq.ai in your browser settings.';
  else if (on) line = `Alerts are on for this ${kind}. Where an event is on for Push below, you get the alert here instead of a text.`;
  else if (status?.thisDevice.registered && !status.thisDevice.working) line = `Alerts stopped working on this ${kind}. Turn them on again.`;
  else line = `Alerts are off for this ${kind}.`;

  const turnOn = async () => {
    setBusy('on');
    try {
      const result = await turnOnPush();
      setPermission(notificationPermission());
      if (result === 'granted') toast.success(`Alerts are on for this ${kind}`);
      else if (result === 'denied') toast.error('Alerts are blocked. You can allow them in your browser settings.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Could not turn on alerts');
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const turnOff = async () => {
    setBusy('off');
    await forgetPushDevice();
    setBusy(null);
    refresh();
    toast.success(`Alerts are off for this ${kind}`);
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const r = unwrap<{ attempted: number; delivered: number }>((await api.push.test()).data);
      if (r && r.delivered > 0) toast.success('Test alert sent — it should appear in a few seconds');
      else if (r && r.attempted > 0) toast.error(`The test alert could not be delivered to this ${kind}`);
      else toast.error('No device is set up for alerts');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not send a test alert');
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const sendDaily = async () => {
    setBusy('daily');
    try {
      const r = unwrap<{ jobs: number; delivered: 'push' | 'sms' | null }>((await api.notificationPreferences.testDailyAlert()).data);
      if (!r || r.jobs === 0) toast.info('No jobs need starting today, so there is no alert to send');
      else if (r.delivered === 'push') toast.success(`Today's daily alert sent to this ${kind}`);
      else if (r.delivered === 'sms') toast.success('Today\'s daily alert sent to you by text');
      else toast.error('Not sent: turn on Push or SMS for Daily Job Alert below, and save');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not send the daily alert');
    } finally {
      setBusy(null);
    }
  };

  const btn = 'bg-fieldiq-card border border-fieldiq-border px-3 py-1.5 rounded-lg text-sm disabled:opacity-50 flex items-center gap-2';
  const canAct = support === 'supported' && status?.webPushEnabled === true;

  return (
    <section aria-labelledby="push-device-heading" className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
      <h2 id="push-device-heading" className="text-sm font-semibold text-white">Alerts on this {kind}</h2>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 size={14} className="animate-spin" /> Checking…</div>
      ) : isError ? (
        <div role="alert" className="flex items-center justify-between gap-3 text-sm text-red-300">
          <span>Could not check this {kind}.</span>
          <button onClick={() => refetch()} className="px-3 py-1.5 rounded-lg border border-red-400/40 text-xs font-semibold hover:bg-red-500/10">Retry</button>
        </div>
      ) : (
        <p className="text-sm text-gray-300" data-testid="push-device-status">{line}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {canAct && !on && permission !== 'denied' && (
          <button onClick={turnOn} disabled={busy !== null} className="bg-fieldiq-accent text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {busy === 'on' && <Loader2 size={14} className="animate-spin" />} Turn on alerts
          </button>
        )}
        {canAct && on && (
          <>
            <button onClick={sendTest} disabled={busy !== null} className={btn}>
              {busy === 'test' && <Loader2 size={14} className="animate-spin" />} Send me a test alert
            </button>
            <button onClick={turnOff} disabled={busy !== null} className={btn}>
              {busy === 'off' && <Loader2 size={14} className="animate-spin" />} Turn off on this {kind}
            </button>
          </>
        )}
        {showDailyAlertTest && (
          <button onClick={sendDaily} disabled={busy !== null} className={btn}>
            {busy === 'daily' && <Loader2 size={14} className="animate-spin" />} Send today&apos;s daily alert to me now
          </button>
        )}
      </div>
      {/* Not in a browser that cannot take alerts at all (or the native shell). */}
      {support !== 'unsupported' && <PushDeviceList />}
    </section>
  );
}
